import React, { useState } from 'react';
import { CanvasMetadata } from '../../services/cloudSave/canvasMetadata';
import { formatDate, formatFileSize, sortCanvases, filterCanvases } from '../../utils/cloudSaveUtils';
import './CloudFileBrowser.css';

interface CloudFileBrowserProps {
  onCanvasSelect: (canvas: CanvasMetadata) => void;
  onCanvasDelete: (canvas: CanvasMetadata, e: React.MouseEvent) => void;
  selectedCanvas: CanvasMetadata | null;
  userCanvases: CanvasMetadata[];
  loading: boolean;
  error: string | null;
  loadingCanvasId?: string | null;
}

const CloudFileBrowser: React.FC<CloudFileBrowserProps> = ({ 
  onCanvasSelect, 
  onCanvasDelete, 
  selectedCanvas,
  userCanvases,
  loading,
  error,
  loadingCanvasId
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'lastModified' | 'name' | 'createdAt'>('lastModified');

  const filteredCanvases = filterCanvases(userCanvases, searchTerm);
  const sortedCanvases = sortCanvases(filteredCanvases, sortBy, 'desc');

  const isCanvasLoading = (canvasId: string) => loadingCanvasId === canvasId;

  return (
    <div className="cloud-load-section">
      <div className="cloud-load-controls">
        <input
          type="text"
          placeholder="Search canvases..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="cloud-search-input"
        />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as any)}
          className="cloud-sort-select"
        >
          <option value="lastModified">Last Modified</option>
          <option value="name">Name</option>
          <option value="createdAt">Created</option>
        </select>
      </div>

      <div className="cloud-canvas-list">
        {loading ? (
          <div className="cloud-loading">
            <div className="loading-spinner"></div>
            <p>Loading your canvases...</p>
            <div className="loading-dots">
              <div className="loading-dot"></div>
              <div className="loading-dot"></div>
              <div className="loading-dot"></div>
            </div>
          </div>
        ) : error ? (
          <div className="load-tree-error">{error}</div>
        ) : sortedCanvases.length === 0 ? (
          <div className="cloud-empty-state">
            <p>No canvases found</p>
          </div>
        ) : (
          sortedCanvases.map((canvas) => (
            <div
              key={canvas.id}
              className={`cloud-canvas-item ${selectedCanvas?.id === canvas.id ? 'selected' : ''} ${isCanvasLoading(canvas.id) ? 'loading' : ''}`}
              onClick={() => onCanvasSelect(canvas)}
            >
              <div className="canvas-item-content">
                {isCanvasLoading(canvas.id) && (
                  <div className="canvas-loading-overlay">
                    <div className="canvas-loading-spinner"></div>
                    <span>Loading...</span>
                  </div>
                )}
                <div className="canvas-item-header">
                  <h4 className="canvas-name">{canvas.name}</h4>
                  <div className="canvas-actions">
                    <button className="canvas-delete-btn" onClick={(e) => onCanvasDelete(canvas, e)} disabled={loading || isCanvasLoading(canvas.id)}>🗑️</button>
                  </div>
                </div>
                <div className="canvas-item-details">
                  <span className="canvas-date">{formatDate(canvas.lastModified.toDate())}</span>
                  <span className="canvas-size">{formatFileSize(canvas.fileSize)}</span>
                  {canvas.description && <span className="canvas-description">{canvas.description}</span>}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CloudFileBrowser;
